import { Box } from '@primer/react';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useColors } from '../../hooks/useColors';
import { useThongKeSiSo } from '../../hooks/useThongKeSiSo';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const ChartSiSoTheoTrangThai = () => {
    const { colors } = useColors();
    const { dmTrangThais, dataStructed, rawTrangThai } = useThongKeSiSo();

    const cosoGroups = useMemo(() => {
        const cosoMap = new Map<number, {
            cosoId: number;
            cosoName: string;
            truongMap: Map<number, { truongId: number; truongName: string }>;
        }>();

        rawTrangThai.forEach((item: any) => {
            if (!cosoMap.has(item.dm_coso_id)) {
                cosoMap.set(item.dm_coso_id, {
                    cosoId: item.dm_coso_id,
                    cosoName: item.ten_co_so || `Cơ sở ${item.dm_coso_id}`,
                    truongMap: new Map()
                });
            }
            const cosoData = cosoMap.get(item.dm_coso_id)!;
            if (!cosoData.truongMap.has(item.dm_truong_id)) {
                cosoData.truongMap.set(item.dm_truong_id, {
                    truongId: item.dm_truong_id,
                    truongName: item.ten_truong
                });
            }
        });

        return Array.from(cosoMap.values());
    }, [rawTrangThai]);

    const chartsData = useMemo(() => {
        let dangTheoHocId: number | undefined;
        let dangTheoHocCoDieuKienId: number | undefined;
        const otherTrangThais: { id: number; name: string }[] = [];

        dmTrangThais.forEach((value: any) => {
            const name = (value.dm_trangthaihocsinh_name || '').toLowerCase().trim();
            if (name === 'đang theo học') {
                dangTheoHocId = value.dm_trangthaihocsinh_id;
            } else if (name === 'đang theo học có điều kiện') {
                dangTheoHocCoDieuKienId = value.dm_trangthaihocsinh_id;
            } else {
                otherTrangThais.push({
                id: value.dm_trangthaihocsinh_id,
                name: value.dm_trangthaihocsinh_name
            });
            }
        });

        const trangThaiList: { id: number; name: string }[] = [];
        if (dangTheoHocId !== undefined) {
            const trangThai = dmTrangThais.get(dangTheoHocId);
            if (trangThai) {
                trangThaiList.push({
                    id: dangTheoHocId,
                    name: trangThai.dm_trangthaihocsinh_name
                });
            }
        }
        if (dangTheoHocCoDieuKienId !== undefined) {
            const trangThai = dmTrangThais.get(dangTheoHocCoDieuKienId);
            if (trangThai) {
                trangThaiList.push({
                    id: dangTheoHocCoDieuKienId,
                    name: trangThai.dm_trangthaihocsinh_name
                });
            }
        }
        trangThaiList.push(...otherTrangThais);

        return cosoGroups.map((cosoGroup) => {
            const truongList: { name: string; trangThaiSum: Map<number, number>; total: number }[] = [];
            const truongIds = Array.from(cosoGroup.truongMap.keys());

            dataStructed.forEach((truong: any, truongIdStr: string) => {
                const truongIdMatch = truongIdStr.match(/TRUONG_(\d+)/);
                const truongId = truongIdMatch ? parseInt(truongIdMatch[1]) : parseInt(truongIdStr);
                if (truongIds.includes(truongId)) {
                    const total = Array.from(truong.trangThaiSum.values() as Iterable<number>).reduce((sum: number, v: number) => sum + v, 0);
            truongList.push({
                name: truong.name,
                        trangThaiSum: truong.trangThaiSum,
                        total
                    });
                }
            });

            const cosoTotal = truongList.reduce((sum: number, truong) => sum + truong.total, 0);

        return {
                cosoId: cosoGroup.cosoId,
                cosoName: cosoGroup.cosoName,
                cosoTotal,
            trangThaiList,
                truongList,
                dangTheoHocId,
                dangTheoHocCoDieuKienId
        };
        });
    }, [dmTrangThais, dataStructed, cosoGroups]);

    const chartsConfig = useMemo(() => {
        return chartsData.map((chartData) => {
            const labels = chartData.trangThaiList.map(x => x.name);

            const datasets = chartData.truongList.map((truong, idx) => {
                const values = chartData.trangThaiList.map(trangThai => {
                return truong.trangThaiSum.get(trangThai.id) || 0;
            });

            const color = colors[idx % colors.length].hex;

            return {
                label: truong.name,
                data: values,
                backgroundColor: color,
                borderColor: color,
                borderWidth: 1,
                maxBarThickness: 24,
                borderRadius: 2,
            };
        });

            const tableRows = chartData.truongList.map(truong => {
                const v1 = chartData.dangTheoHocId !== undefined ? (truong.trangThaiSum.get(chartData.dangTheoHocId) || 0) : 0;
                const v2 = chartData.dangTheoHocCoDieuKienId !== undefined ? (truong.trangThaiSum.get(chartData.dangTheoHocCoDieuKienId) || 0) : 0;
                const hienHuu = v1 + v2;

                const values = chartData.trangThaiList.map(trangThai => {
                    return truong.trangThaiSum.get(trangThai.id) || 0;
                });
                const total = values.reduce((sum: number, v: number) => sum + v, 0);
        return {
                    name: truong.name,
                    hienHuu,
                    values,
                    total
        };
            });

            const trangThaiTotals = chartData.trangThaiList.map(trangThai => {
                const total = chartData.truongList.reduce((sum: number, truong) => {
                    return sum + (truong.trangThaiSum.get(trangThai.id) || 0);
                }, 0);
                return {
                    id: trangThai.id,
                    name: trangThai.name,
                    total
                };
            });

            const hienHuuTotal = tableRows.reduce((sum: number, row) => sum + row.hienHuu, 0);

            const totalAll = tableRows.reduce((sum: number, row) => sum + row.total, 0);

            return {
                ...chartData,
                chartData: {
                    labels,
                    datasets
                },
                tableRows,
                trangThaiTotals,
                hienHuuTotal,
                totalAll
            };
        });
    }, [chartsData, colors]);
    const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 24
            }
        },
        plugins: {
            legend: {
                display: false
            },
            datalabels: {
                display: true,
                color: 'black',
                anchor: 'end',
                align: 'end',
                offset: 4,
                clamp: true,
                clip: false,
                font: {
                    weight: 'bold',
                    size: 13,
                },
            },
        },
        scales: {
            x: {
                stacked: false,
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            },
            y: {
                beginAtZero: true,
                grace: '10%',
                grid: {
                    color: '#eeeeee'
                },
                ticks: {
                    stepSize: 100,
                    font: {
                        size: 11
                    }
                }
            }
        }
    };
    return (
        <>
            <Box sx={{ fontSize: 24, fontWeight: 600 }}>Trạng thái học sinh</Box>
            <Box sx={{ fontSize: "1rem", color: "fg.muted" }}>Số học sinh theo các trạng thái</Box>
            <Box sx={{ mt: 3, display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 4 }}>
                {chartsConfig.map((chartConfig, idx) => (
                    <Box key={chartConfig.cosoId} sx={{ flex: chartsConfig.length > 1 ? "1 1 calc(50% - 16px)" : "1 1 100%", minWidth: "400px" }}>
                        {chartsConfig.length > 1 && (
                            <Box sx={{ fontSize: 18, fontWeight: 600, mb: 2, display: "flex", alignItems: "center" }}>
                                <Box>{chartConfig.cosoName}</Box>
                                <Box sx={{ ml: 1, color: "fg.muted", fontSize: 16 }}>({chartConfig.cosoTotal})</Box>
                            </Box>
                        )}
                <Box sx={{ height: "300px" }}>
                            <Bar data={chartConfig.chartData} options={options} />
                        </Box>
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 2 }}>
                            {chartConfig.truongList.map((truong, truongIdx) => {
                                const color = colors[truongIdx % colors.length].hex;
                                const truongTotal = chartConfig.tableRows.find(r => r.name === truong.name)?.total || 0;
                                return (
                                    <Box
                                        key={truongIdx}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: 12
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: 0,
                                                backgroundColor: color,
                                                mr: 1
                                            }}
                                        />
                                        <Box sx={{ fontWeight: 500 }}>{truong.name}</Box>
                                        <Box sx={{ ml: 1, color: "fg.muted" }}>({truongTotal})</Box>
                </Box>
                                );
                            })}
            </Box>
            <Box sx={{ mt: 3, overflowX: "auto" }}>
                <Box
                    as="table"
                    sx={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 12,
                    }}
                >
                    <Box as="thead">
                        <Box as="tr">
                            <Box
                                as="th"
                                sx={{
                                    border: "1px solid",
                                    borderColor: "border.muted",
                                    px: 2,
                                    py: 1,
                                    textAlign: "left",
                                    backgroundColor: "canvas.subtle",
                                    fontWeight: 600
                                }}
                            >
                                
                            </Box>
                            <Box
                                as="th"
                                sx={{
                                    border: "1px solid",
                                    borderColor: "border.muted",
                                    px: 2,
                                    py: 1,
                                    textAlign: "right",
                                    backgroundColor: "canvas.subtle",
                                    fontWeight: 600,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    maxWidth: "120px"
                                }}
                            >
                                Hiện hữu
                            </Box>
                            {chartConfig.trangThaiList.map((t) => {
                                return (
                                    <Box
                                        as="th"
                                        key={t.id}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "border.muted",
                                            px: 2,
                                            py: 1,
                                            textAlign: "right",
                                            backgroundColor: "canvas.subtle",
                                            fontWeight: 600,
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                            maxWidth: "120px"
                                        }}
                                    >
                                        {t.name}
                                    </Box>
                                );
                            })}
                            <Box
                                as="th"
                                sx={{
                                    border: "1px solid",
                                    borderColor: "border.muted",
                                    px: 2,
                                    py: 1,
                                    textAlign: "right",
                                    backgroundColor: "canvas.subtle",
                                    fontWeight: 600,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                maxWidth: "120px"
                                }}
                            >
                                            Tổng số 
                            </Box>
                        </Box>
                    </Box>
                    <Box as="tbody">
                                    {chartConfig.tableRows.map((row, rowIdx) => (
                                        <Box as="tr" key={rowIdx}>
                                <Box
                                    as="td"
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "border.muted",
                                        px: 2,
                                        py: 1,
                                        textAlign: "left",
                                        fontWeight: 500
                                    }}
                                >
                                    {row.name}
                                </Box>
                                            <Box
                                                as="td"
                                                sx={{
                                                    border: "1px solid",
                                                    borderColor: "border.muted",
                                                    px: 2,
                                                    py: 1,
                                                    textAlign: "right",
                                                    fontWeight: 600
                                                }}
                                            >
                                                {row.hienHuu}
                                            </Box>
                                {row.values.map((v, i) => (
                                    <Box
                                        as="td"
                                        key={i}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "border.muted",
                                            px: 2,
                                            py: 1,
                                            textAlign: "right"
                                        }}
                                    >
                                        {v}
                                    </Box>
                                ))}
                                <Box
                                    as="td"
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "border.muted",
                                        px: 2,
                                        py: 1,
                                        textAlign: "right",
                                        fontWeight: 600
                                    }}
                                >
                                    {row.total}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </>
    );
};

export default ChartSiSoTheoTrangThai;
